import { DestroyRef, EnvironmentProviders, inject, makeEnvironmentProviders, provideEnvironmentInitializer } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthServiceFacade } from '@pcp/auth';
import { PCP_LOGGER } from '@pcp/logging';
import { DebugConfigService, updateDebugAuthorization } from './debug-config';

/**
 * Wires up the unified debug mode for an app:
 *
 * - **Authorization** — subscribes to the JWT `customer` claim (internal accounts only,
 *   see {@link updateDebugAuthorization}).
 * - **Shortcut** — installs the single app-wide Ctrl+Shift+D toggle (`e.code === 'KeyD'`,
 *   layout/case independent), gated on {@link DebugConfigService.canToggle}. Reactive
 *   consumers update immediately; canvases repaint via their own `enabled` effects.
 *
 * Register once in the bootstrap providers of every app that hosts debug-gated features.
 * Apps without it keep the `isDevMode()` fallback (debug off in production) and have no
 * toggle shortcut.
 *
 * Kept separate from `debug-config.ts` so plain `isDebugEnabled()` consumers don't pull
 * `@pcp/auth` transitively.
 */
export function provideDebugConfig(): EnvironmentProviders {
    return makeEnvironmentProviders([
        provideEnvironmentInitializer(() => {
            inject(AuthServiceFacade)
                .customer$.pipe(takeUntilDestroyed())
                .subscribe((customer) => updateDebugAuthorization(customer));

            const debugConfig = inject(DebugConfigService);
            const log = inject(PCP_LOGGER, { optional: true })?.createScoped('debug-config');
            const keydownListener = (e: KeyboardEvent) => {
                if (!(debugConfig.canToggle() && e.ctrlKey && e.shiftKey && e.code === 'KeyD')) return;
                const enabled = debugConfig.toggle();
                log?.debug(`Debug mode: ${enabled ? 'ON' : 'OFF'}`);
                e.preventDefault();
            };
            document.addEventListener('keydown', keydownListener);
            inject(DestroyRef).onDestroy(() => document.removeEventListener('keydown', keydownListener));
        }),
    ]);
}
